import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard } from "@/lib/api";
import { ISSUE_TYPE_CONFIG, STATUS_COLORS, STATUS_LABELS, PRIORITY_COLORS } from "@/lib/constants";
import { ArrowRight, FolderKanban, CheckCircle2, Clock, Layers } from "lucide-react";

export default function Dashboard({ users, projects }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboard().then(setData).catch(console.error);
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  const getUserName = (id) => users.find((u) => u.id === id)?.full_name || "Unassigned";

  return (
    <div className="h-full overflow-y-auto" data-testid="dashboard">
      <div className="max-w-5xl mx-auto p-8 space-y-10">
        {/* Header */}
        <div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tight leading-none"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-2">Overview of your projects and recent activity</p>
        </div>

        {/* Project Cards */}
        <div className="space-y-4">
          <h2
            className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500"
          >
            Projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.project_stats.map((stat) => {
              const pct = stat.total_issues > 0 ? Math.round((stat.done / stat.total_issues) * 100) : 0;
              return (
                <div
                  key={stat.project.id}
                  data-testid={`dashboard-project-${stat.project.key}`}
                  className="border border-gray-200 bg-white p-5 rounded-sm hover:border-brand-rose transition-colors cursor-pointer group"
                  onClick={() => navigate(`/project/${stat.project.key}/backend`)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <FolderKanban className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-mono font-bold text-gray-400">{stat.project.key}</span>
                      </div>
                      <h3
                        className="text-base font-bold mt-1 text-black"
                        style={{ fontFamily: "Chivo, sans-serif" }}
                      >
                        {stat.project.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{stat.project.description}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-brand-rose transition-colors" />
                  </div>
                  <div className="flex items-center gap-5 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">{stat.total_issues} issues</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-xs font-medium text-gray-600">{stat.in_progress} in progress</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="text-xs font-medium text-gray-600">{pct}% done</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Issues */}
        <div className="space-y-4">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
            Recent Issues
          </h2>
          <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
            {data.recent_issues.map((issue) => {
              const typeConf = ISSUE_TYPE_CONFIG[issue.type] || {};
              const statusConf = STATUS_COLORS[issue.status] || {};
              const priorityConf = PRIORITY_COLORS[issue.priority] || {};
              return (
                <div
                  key={issue.id}
                  data-testid={`recent-issue-${issue.issue_key}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    const boardMap = {
                      INITIATIVE: "initiatives",
                      DESIGN: "design",
                      FRONTEND: "frontend",
                      BACKEND: "backend",
                      BUG: "bugs",
                      QA: "backend",
                    };
                    navigate(`/project/${issue.project_key}/${boardMap[issue.type] || "backend"}?issue=${issue.issue_key}`);
                  }}
                >
                  <span
                    className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: typeConf.bg,
                      color: typeConf.text,
                      borderColor: typeConf.bg,
                    }}
                  >
                    {typeConf.label}
                  </span>
                  <span className="text-xs font-mono text-gray-400 w-16 flex-shrink-0">
                    {issue.issue_key}
                  </span>
                  <span className="text-sm text-black flex-1 truncate font-medium">
                    {issue.title}
                  </span>
                  <span
                    className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: priorityConf.bg,
                      color: priorityConf.text,
                      borderColor: priorityConf.border,
                    }}
                  >
                    {issue.priority}
                  </span>
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: statusConf.bg,
                      color: statusConf.text,
                      borderColor: statusConf.border,
                    }}
                  >
                    {STATUS_LABELS[issue.status]}
                  </span>
                  <span className="text-xs text-gray-400 w-24 text-right">
                    {getUserName(issue.assignee_id)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
